import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { Add, Delete } from "@material-ui/icons";
import { css } from "@emotion/css";
import { Link, useHistory } from "react-router-dom";
import { Area } from "types";
import { useListLessons } from "routes/createContent/api/useListLessons";
import { useDeleteLesson } from "routes/createContent/api/useDeleteLesson";
import { LOCALES, LESSON_STATES, TARGET_JOINTS } from "../constants";
import { getErrorMessage } from "../utils";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";

const LessonsList: React.VFC = () => {
  const history = useHistory();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [joints, setJoints] = useState<string[]>([]);
  const [stateFilter, setStateFilter] = useState("all");
  const [locale, setLocale] = useState(LOCALES[0].code);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const del = useDeleteLesson();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [joints, stateFilter, locale]);

  const { data, isLoading, isError, error } = useListLessons({
    page: page + 1,
    limit,
    search: debouncedSearch,
    joints,
    state: stateFilter === "all" ? undefined : stateFilter,
    locale,
  });

  const items = data?.items ?? [];
  const totalCount = data?.metadata?.total_count ?? items.length;

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <h2 style={{ margin: 0 }}>Lessons</h2>
        <Button color="primary" variant="contained" startIcon={<Add />} component={Link} to={Area.Lessons_New}>
          New lesson
        </Button>
      </Box>

      <Box mb={2} display="flex" style={{ gap: 12 }}>
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search by title"
          style={{ flex: 1 }}
        />
        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
          <InputLabel>Joint</InputLabel>
          <Select
            multiple
            label="Joint"
            value={joints}
            onChange={e => setJoints(e.target.value as string[])}
            renderValue={selected => {
              const sel = selected as string[];
              return sel.length === 0 ? "All" : sel.join(", ");
            }}
          >
            {TARGET_JOINTS.map(j => (
              <MenuItem key={j} value={j}>
                {j}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {joints.length > 0 && (
          <Button size="small" onClick={() => setJoints([])}>
            Clear
          </Button>
        )}
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>State</InputLabel>
          <Select label="State" value={stateFilter} onChange={e => setStateFilter(e.target.value as string)}>
            <MenuItem value="all">All</MenuItem>
            {LESSON_STATES.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
          <InputLabel>Locale</InputLabel>
          <Select label="Locale" value={locale} onChange={e => setLocale(e.target.value as string)}>
            {LOCALES.map(l => (
              <MenuItem key={l.code} value={l.code}>
                {l.flag} {l.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {isError && (
        <Box color="error.main" my={2}>
          Failed to load: {getErrorMessage(error)}
        </Box>
      )}
      {del.isError && (
        <Box color="error.main" my={2}>
          {getErrorMessage(del.error)}
        </Box>
      )}

      {!isLoading && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 60 }}>Image</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Joints</TableCell>
                <TableCell style={{ width: 100 }}>State</TableCell>
                <TableCell style={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(lesson => (
                <TableRow
                  key={lesson.id}
                  hover
                  className={styles.row}
                  onClick={() => history.push(Area.Lessons_Edit.replace(":id", String(lesson.id)))}
                >
                  <TableCell>
                    {lesson.image_url ? (
                      <img src={lesson.image_url} alt="" className={styles.thumb} />
                    ) : (
                      <Box className={styles.noImage}>--</Box>
                    )}
                  </TableCell>
                  <TableCell>{lesson.title ?? `Lesson ${lesson.id}`}</TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                      {lesson.target_joints.map(j => (
                        <Chip key={j} label={j} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={lesson.state} size="small" className={styles[lesson.state] ?? ""} />
                  </TableCell>
                  <TableCell padding="none">
                    <Tooltip title="Delete lesson">
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          del.reset();
                          setConfirmDelete(lesson.id);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No lessons found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100, 200, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={limit}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
      <ConfirmDeleteDialog
        open={confirmDelete != null}
        loading={del.isLoading}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => {
          if (confirmDelete == null) return;
          del.mutate(confirmDelete, {
            onSuccess: () => setConfirmDelete(null),
          });
        }}
      />
    </div>
  );
};

export default LessonsList;

const styles = {
  row: css`
    cursor: pointer;
  `,
  thumb: css`
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
  `,
  noImage: css`
    width: 48px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    font-size: 12px;
  `,
  draft: css`
    background-color: #e0e0e0 !important;
  `,
  in_review: css`
    background-color: #fff3e0 !important;
    color: #e65100 !important;
  `,
  approved: css`
    background-color: #e8f5e9 !important;
    color: #2e7d32 !important;
  `,
  rejected: css`
    background-color: #ffebee !important;
    color: #c62828 !important;
  `,
};
